import { useState, useEffect, useCallback } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Fuel, TrendingUp, ArrowUp, ArrowDown, Minus, Loader2, ChevronLeft } from "lucide-react";
import { api } from "../lib/api";
import { SaudiRiyal } from "./SaudiRiyal";
import { useLang } from "../i18n/LanguageContext";

// Fleet fuel-cost dashboard (moved here from the Vehicles tab) — total + top consuming plates,
// clickable for a per-vehicle breakdown (current vs previous month + monthly average).
export const FleetFuelCost = ({ month: pMonth, rev } = {}) => {
  const { t, dir } = useLang();
  const [fuel, setFuel] = useState({ month: null, months: [], totals: {}, fleet_total: 0, count: 0, top: [] });
  const [fuelMonth, setFuelMonth] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState({ month: null, prev_month: null, rows: [] });
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    try { setFuel(await api.vehiclesFuelCosts(fuelMonth || pMonth)); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fuelMonth, pMonth, rev]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async () => {
    setDetailOpen(true); setDetailLoading(true);
    try { setDetail(await api.vehiclesFuelDetail(fuel.month)); }
    catch { setDetail({ month: fuel.month, prev_month: null, rows: [] }); }
    finally { setDetailLoading(false); }
  };

  if (!fuel.months?.length) return null;

  return (
    <div className="space-y-3" dir={dir}>
      <div className="flex justify-end">
        <Select value={fuel.month || ""} onValueChange={setFuelMonth}>
          <SelectTrigger data-testid="fleet-fuel-month-select" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            {fuel.months.map((m) => <SelectItem key={m} value={m}>{t("dash.vehicles.fuelMonth", { m })}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <button type="button" onClick={openDetail} data-testid="fleet-fuel-dashboard"
        className="w-full text-start rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-5 transition hover:border-emerald-300 hover:shadow-md hover:shadow-emerald-100/50 cursor-pointer group">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
              <Fuel className="h-4 w-4 text-emerald-600" /> {t("dash.vehicles.fleetTitle")}
              <span className="text-xs font-normal text-emerald-600 opacity-0 group-hover:opacity-100 transition inline-flex items-center gap-0.5">{t("dash.vehicles.viewDetails")} <ChevronLeft className="h-3 w-3" /></span>
            </p>
            <p className="text-xs text-slate-400 mt-0.5">{t("dash.vehicles.fuelMonth", { m: fuel.month || "—" })}</p>
          </div>
          <div className="text-end">
            <p className="text-xs text-slate-500">{t("dash.vehicles.fleetTotal")}</p>
            <p className="font-heading text-3xl font-bold text-emerald-700" data-testid="fleet-total">
              {(fuel.fleet_total || 0).toLocaleString()} <SaudiRiyal className="text-slate-400" />
            </p>
            <p className="text-[11px] text-slate-400">{t("dash.vehicles.fleetVehicles", { n: fuel.count || 0 })}</p>
          </div>
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5" /> {t("dash.vehicles.fleetTop")}</p>
          {(!fuel.top || fuel.top.length === 0) ? (
            <p className="text-sm text-slate-400" data-testid="fleet-top-empty">{t("dash.vehicles.fleetNoData")}</p>
          ) : (
            <div className="space-y-2" data-testid="fleet-top-list">
              {fuel.top.map((it, i) => {
                const max = fuel.top[0].total || 1;
                return (
                  <div key={it.plate} className="flex items-center gap-3" data-testid={`fleet-top-${i}`}>
                    <span className="w-6 text-center text-xs font-bold text-slate-400">{i + 1}</span>
                    <span className="w-24 shrink-0 font-mono-nums font-semibold text-slate-700 text-sm truncate" dir="ltr">{it.plate}</span>
                    <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all"
                        style={{ width: `${Math.max(6, (it.total / max) * 100)}%` }} />
                    </div>
                    <span className="w-24 text-end shrink-0 font-mono-nums font-semibold text-emerald-700 text-sm">{it.total.toLocaleString()} <SaudiRiyal className="opacity-70" /></span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </button>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-2xl font-body max-h-[90vh] overflow-y-auto" dir={dir}>
          <DialogHeader className="text-start">
            <DialogTitle className="font-heading text-xl flex items-center gap-2"><Fuel className="h-5 w-5 text-emerald-600" /> {t("dash.vehicles.detailTitle")}</DialogTitle>
            <DialogDescription className="text-start">{t("dash.vehicles.detailDesc", { m: detail.month || "—", p: detail.prev_month || "—" })}</DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="py-12 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : detail.rows.length === 0 ? (
            <div className="py-12 text-center text-slate-400" data-testid="fuel-detail-empty">{t("dash.vehicles.fleetNoData")}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="fuel-detail-table" style={{ minWidth: 640 }}>
                <thead>
                  <tr className="border-b border-slate-100 text-slate-500">
                    <th className="p-2.5 text-start font-semibold">{t("dash.vehicles.colPlate")}</th>
                    <th className="p-2.5 text-end font-semibold">{t("dash.vehicles.detailCurrent")}</th>
                    <th className="p-2.5 text-end font-semibold">{t("dash.vehicles.detailPrev")}</th>
                    <th className="p-2.5 text-end font-semibold">{t("dash.vehicles.detailChange")}</th>
                    <th className="p-2.5 text-end font-semibold">{t("dash.vehicles.detailAvg")}</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.rows.map((r) => {
                    const up = r.delta > 0, down = r.delta < 0;
                    const Ico = up ? ArrowUp : down ? ArrowDown : Minus;
                    const col = up ? "text-red-600" : down ? "text-emerald-600" : "text-slate-400";
                    return (
                      <tr key={r.plate} data-testid={`fuel-detail-row-${r.plate}`} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60">
                        <td className="p-2.5 font-semibold text-slate-800" dir="ltr">{r.plate}</td>
                        <td className="p-2.5 text-end font-mono-nums text-slate-700">{r.current.toLocaleString()}</td>
                        <td className="p-2.5 text-end font-mono-nums text-slate-400">{r.previous.toLocaleString()}</td>
                        <td className="p-2.5 text-end">
                          <span className={`inline-flex items-center gap-0.5 font-mono-nums font-semibold ${col}`}>
                            <Ico className="h-3.5 w-3.5" />{Math.abs(r.pct)}%
                          </span>
                        </td>
                        <td className="p-2.5 text-end font-mono-nums text-slate-600">{r.avg.toLocaleString()} <span className="text-[10px] text-slate-400">/{t("dash.vehicles.perMonth")}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-[11px] text-slate-400 mt-3">{t("dash.vehicles.detailNote")}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
